import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {Check, Plus, Minus, ArrowRight, Sparkles, Heart, ShieldCheck, Leaf, Clock, MapPin} from 'lucide-react'

const HEADSHOT = 'https://static.lumi.new/29/291214acedc9520c3e2ce18a31832558.webp'
const JANE_APP = 'https://havenintegrativehealth.janeapp.com/login'

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Menopause Care', href: '#care' },
  { label: 'Symptoms', href: '#symptoms' },
  { label: 'Treatment Options', href: '#treatment' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
]

const symptoms = [
  'Hot flashes',
  'Night sweats',
  'Sleep changes',
  'Mood changes',
  'Brain fog',
  'Vaginal dryness',
  'Urinary concerns',
  'Bone health',
  'Cholesterol & heart health risk review',
]

const approachSteps = [
  { n: '01', title: 'Initial Assessment', body: 'Thorough intake covering symptoms, history, and STRAW staging to understand exactly where you are.' },
  { n: '02', title: 'Medication & Supplement Review', body: 'A pharmacist-led review of every medication and supplement to ensure safety and synergy.' },
  { n: '03', title: 'NP Referral & Lab Recommendations', body: 'Where lab work or HRT treatment consideration is clinically appropriate, we coordinate a referral to our nurse practitioner for further assessment and prescribing support.' },
  { n: '04', title: 'Lab & Health History Review', body: 'Lab work and health history reviewed when clinically appropriate to inform your plan.' },
  { n: '05', title: 'Eligibility Screening', body: 'Hormone therapy and treatment eligibility assessed against your individual risk profile.' },
  { n: '06', title: 'Personalized Care Plan', body: 'A clear, evidence-informed plan with education, lifestyle support, and ongoing follow-up.' },
]

const supportPillars = [
  { icon: Heart, title: 'Lifestyle & Preventative Health', body: 'Sustainable strategies built around your daily life and long-term wellbeing.' },
  { icon: Sparkles, title: 'Strength Training Guidance', body: 'Practical guidance to protect bone density, muscle mass, and metabolic health.' },
  { icon: Leaf, title: 'Nutritional Deficiency Review', body: 'Iron, vitamin B12, and vitamin D reviewed and supported when clinically indicated.' },
  { icon: ShieldCheck, title: 'Bone & Cardiovascular Education', body: 'Clear, evidence-informed education on long-term bone and heart health risk.' },
]

const treatmentSupport = [
  'Hormone therapy education',
  'Non-hormonal options',
  'Vaginal estrogen education',
  'Lifestyle support',
  'Vitamin D3 & K2',
  'Magnesium glycinate',
  'Collagen support',
  'Strength training guidance',
  'Referral recommendations when needed',
]

const whyHaven = [
  'Pharmacist-led care',
  'Preventative health focus',
  'Personalized education and follow-up',
  'Concierge-style support',
  'Flexible virtual care options',
  'Free Lower Mainland delivery where applicable',
  'Barrier-free access to evidence-informed menopause support',
]

const faqs = [
  { q: 'What is the cost?', a: 'Initial Menopause Assessment is $195 (45 minutes). Follow-up consultations are $95 (30 minutes). Extended follow-ups may be recommended depending on individual care needs.' },
  { q: 'Is this covered by insurance?', a: 'Coverage varies by plan. Only HSA (Health Spending Account) coverage may apply. We provide detailed receipts on request so you can submit them to your insurer. Please check with your provider directly to confirm what is covered.' },
  { q: 'What makes Haven Menopause different?', a: 'Pharmacist-led, preventative, and concierge-style. We focus on individualized assessment, comprehensive medication review, and evidence-informed education rather than a one-size-fits-all template.' },
  { q: 'What symptoms and concerns do you support?', a: 'Hot flashes, night sweats, sleep changes, mood changes, brain fog, vaginal dryness, urinary concerns, bone health, and cardiovascular risk review.' },
  { q: 'How does the pharmacist-led process work?', a: 'Our pharmacist conducts a thorough individualized assessment, reviews medications and supplements, screens for treatment eligibility, and works alongside our nurse practitioner when prescriptions are clinically appropriate.' },
  { q: 'Do I need a referral?', a: 'No referral is required. You can book directly with Haven and we will guide you from there.' },
  { q: 'Does this replace my GP or family doctor?', a: 'No. Haven Menopause is designed to supplement your existing care, not replace it. We work alongside your primary care provider to fill gaps in menopause support. Upon request, we can share a summary of your assessment and recommendations with your provider.' },
  { q: 'Is care virtual or in person?', a: 'Care is delivered virtually for convenience and accessibility, with free Lower Mainland delivery where applicable.' },
  { q: 'How long until I see results?', a: 'Some people notice improvement within weeks; others require longer follow-up and adjustment. Your care plan includes ongoing review and refinement.' },
]

export default function MenopauseHealth() {
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [navOpen, setNavOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="min-h-screen bg-[#FBF7F4] text-[#2A1F2D] font-sans overflow-x-hidden">
      {/* DEDICATED MENOPAUSE NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#FBF7F4]/95 backdrop-blur-md shadow-[0_1px_0_rgba(74,46,86,0.08)]' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5B3A6B] to-[#8E6A9B] flex items-center justify-center text-white text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              H
            </div>
            <div className="leading-tight">
              <div className="text-[15px] tracking-[0.2em] uppercase text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Haven</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]/80">Menopause</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(l => (
              l.href.startsWith('#') ? (
                <a key={l.label} href={l.href} className="text-[12px] tracking-[0.18em] uppercase text-[#3D2C44] hover:text-[#5B3A6B] transition-colors">
                  {l.label}
                </a>
              ) : (
                <Link key={l.label} to={l.href} className="text-[12px] tracking-[0.18em] uppercase text-[#3D2C44] hover:text-[#5B3A6B] transition-colors">
                  {l.label}
                </Link>
              )
            ))}
            <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="ml-2 px-5 py-2.5 rounded-full bg-[#5B3A6B] text-white text-[11px] tracking-[0.2em] uppercase hover:bg-[#4A2E56] transition-all hover:shadow-[0_8px_24px_rgba(91,58,107,0.3)]">
              Book Consultation
            </a>
          </div>

          <button onClick={() => setNavOpen(!navOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2A1F2D]" aria-label="Toggle menu">
            <div className="space-y-1.5">
              <span className={`block w-5 h-px bg-current transition-all ${navOpen ? 'rotate-45 translate-y-[6px]' : ''}`} />
              <span className={`block w-5 h-px bg-current transition-all ${navOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-px bg-current transition-all ${navOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} />
            </div>
          </button>
        </div>

        <AnimatePresence>
          {navOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-[#FBF7F4] border-t border-[#5B3A6B]/10">
              <div className="px-5 py-6 space-y-4">
                {navLinks.map(l => (
                  l.href.startsWith('#') ? (
                    <a key={l.label} href={l.href} onClick={() => setNavOpen(false)} className="block text-[13px] tracking-[0.2em] uppercase text-[#3D2C44] py-1">
                      {l.label}
                    </a>
                  ) : (
                    <Link key={l.label} to={l.href} onClick={() => setNavOpen(false)} className="block text-[13px] tracking-[0.2em] uppercase text-[#3D2C44] py-1">
                      {l.label}
                    </Link>
                  )
                ))}
                <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-block px-5 py-2.5 rounded-full bg-[#5B3A6B] text-white text-[11px] tracking-[0.2em] uppercase">
                  Book Consultation
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
        {/* Soft gradient backdrop */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F4ECEF] via-[#FBF7F4] to-[#EFE6F0]" />
          <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-[radial-gradient(ellipse_at_top_right,_rgba(143,107,158,0.18),_transparent_60%)]" />
          <div className="absolute bottom-0 left-0 w-[50%] h-[60%] bg-[radial-gradient(ellipse_at_bottom_left,_rgba(212,180,160,0.22),_transparent_55%)]" />
        </div>

        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT — Copy */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="lg:col-span-7 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-[#5B3A6B]/15 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5B3A6B]" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#5B3A6B]">Haven Menopause Care</span>
            </div>

            <h1 className="text-[clamp(2.25rem,6vw,4.75rem)] leading-[1.02] tracking-[-0.02em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Pharmacist-Led<br />
              <em className="not-italic text-[#5B3A6B]">Menopause Care,</em><br />
              Designed Around You
            </h1>

            <p className="mt-7 text-[16px] sm:text-[17px] leading-[1.7] text-[#4A3D52] max-w-[560px]">
              Personalized support for perimenopause, menopause, and post-menopause symptoms with evidence-informed guidance, treatment review, and free Lower Mainland delivery designed to reduce barriers to care.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#5B3A6B] text-white text-[12px] tracking-[0.2em] uppercase hover:bg-[#4A2E56] transition-all hover:shadow-[0_12px_30px_rgba(91,58,107,0.35)]">
                Book a Menopause Consultation <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#care" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/70 backdrop-blur border border-[#5B3A6B]/20 text-[#2A1F2D] text-[12px] tracking-[0.2em] uppercase hover:bg-white transition-all">
                Learn More
              </a>
            </div>

            {/* Bio card near hero */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }} className="mt-10 max-w-[560px] p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/80 shadow-[0_20px_60px_-20px_rgba(91,58,107,0.18)]">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-px bg-[#5B3A6B]" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#5B3A6B]">Member · NAMS</span>
              </div>
              <p className="text-[14px] leading-[1.7] text-[#3D2C44]">
                Member of <span className="text-[#5B3A6B]">The North American Menopause Society</span>. Our expert pharmacist and nurse practitioner conduct thorough individualized assessments with appropriate lab work and health review when clinically indicated to support evidence-informed menopause care.
              </p>
            </motion.div>
          </motion.div>

          {/* RIGHT — Big Headshot */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5 order-1 lg:order-2 relative">
            <div className="relative">
              {/* Decorative blob behind */}
              <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-[#E8DCEA] via-[#F2E5DA] to-[#DDC9E2] blur-2xl opacity-80" />
              <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#D4B4A0]/40 blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-[#8E6A9B]/30 blur-2xl" />

              {/* Image frame */}
              <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] w-full max-w-[480px] lg:max-w-none mx-auto overflow-hidden rounded-[1.5rem] shadow-[0_30px_80px_-20px_rgba(74,46,86,0.35)]">
                <img src={HEADSHOT} alt="Dr. Saba Baig — Founder, Haven Integrative Health" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A1F2D]/30 via-transparent to-transparent" />
              </div>

              {/* Floating badge */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="absolute -bottom-5 -left-5 sm:-left-8 lg:-left-12 px-5 py-4 rounded-2xl bg-white shadow-[0_20px_50px_-15px_rgba(74,46,86,0.3)] max-w-[230px]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8E6A9B] mb-1">Founder</div>
                <div className="text-[15px] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Dr. Saba Baig</div>
                <div className="text-[11px] text-[#4A3D52] mt-0.5">PharmD · NAMS Member</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EDITORIAL IMAGE BAND */}
      <section className="py-12 lg:py-16 w-full overflow-hidden">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="w-full">
          <img
            src="https://static.lumi.new/ba/ba04bb892c46747d56f35357ba44d882.webp"
            alt="Women supported by Haven Integrative Health menopause care"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </motion.div>
      </section>

      {/* WHAT WE HELP WITH — symptoms */}
      <section id="symptoms" className="py-20 lg:py-28 relative">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="max-w-[700px] mb-14">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">What We Help With</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Comprehensive support for the symptoms that shape your day.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
            {symptoms.map((s, i) => (
              <motion.div key={s} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.04 }} className="group p-5 sm:p-6 rounded-2xl bg-white border border-[#5B3A6B]/8 hover:border-[#5B3A6B]/25 hover:shadow-[0_20px_50px_-25px_rgba(91,58,107,0.3)] transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F2E5DA] to-[#E8DCEA] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Sparkles className="w-4 h-4 text-[#5B3A6B]" />
                  </div>
                  <span className="text-[14px] sm:text-[15px] text-[#2A1F2D]">{s}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section id="care" className="py-20 lg:py-28 bg-gradient-to-b from-[#F4ECEF] to-[#FBF7F4]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="max-w-[700px] mb-16">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">Our Approach</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              A measured, evidence-informed path to feeling like yourself again.
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
            {approachSteps.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08 }} className="p-6 sm:p-7 rounded-2xl bg-white border border-[#5B3A6B]/8 hover:shadow-[0_30px_70px_-30px_rgba(91,58,107,0.3)] transition-all">
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#8E6A9B] mb-4">{s.n}</div>
                <h3 className="text-[20px] leading-tight text-[#2A1F2D] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{s.title}</h3>
                <p className="text-[13.5px] leading-[1.7] text-[#4A3D52]">{s.body}</p>
              </motion.div>
            ))}
          </div>

          {/* Support pillars */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {supportPillars.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06 }} className="p-6 rounded-2xl bg-gradient-to-br from-[#FBF7F4] to-[#F4ECEF] border border-white">
                <p.icon className="w-7 h-7 text-[#5B3A6B] mb-4" strokeWidth={1.4} />
                <h4 className="text-[16px] text-[#2A1F2D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{p.title}</h4>
                <p className="text-[13px] leading-[1.65] text-[#4A3D52]">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENT SUPPORT */}
      <section id="treatment" className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">Treatment Support</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Hormonal, non-hormonal, and lifestyle options reviewed for you.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.75] text-[#4A3D52] max-w-[520px]">
              Treatment eligibility depends on your full history. We review every option carefully and provide clear, individualized recommendations alongside referrals when needed.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="grid grid-cols-2 gap-3">
            {treatmentSupport.map((t, i) => (
              <div key={t} className="px-4 py-4 rounded-xl bg-white border border-[#5B3A6B]/10 hover:border-[#5B3A6B]/30 transition-all">
                <div className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#5B3A6B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-[13.5px] text-[#2A1F2D] leading-snug">{t}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-28 bg-gradient-to-br from-[#5B3A6B] via-[#4A2E56] to-[#3D2444] text-white relative overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#D4B4A0]/30 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[#8E6A9B]/40 blur-3xl" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center max-w-[700px] mx-auto mb-16">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4B4A0]">Pricing</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Transparent, concierge-style pricing.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.75] text-white/75">
              Personalized menopause assessment and follow-up care, priced clearly with no hidden fees.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-[1100px] mx-auto">
            {/* Initial */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-[#FBF7F4] to-[#F4ECEF] text-[#2A1F2D] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]">
              <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-[#5B3A6B] text-white text-[9px] tracking-[0.25em] uppercase">Most Comprehensive</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B] mb-3">Initial Assessment</div>
              <h3 className="text-[28px] leading-tight mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Initial Menopause Assessment</h3>
              <div className="flex items-baseline gap-3 mt-5 mb-7">
                <span className="text-[44px] leading-none text-[#5B3A6B]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>$195</span>
                <span className="text-[12px] tracking-[0.2em] uppercase text-[#4A3D52] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 45 Minutes</span>
              </div>
              <div className="h-px bg-[#5B3A6B]/15 mb-6" />
              <ul className="space-y-3.5">
                {[
                  'Symptom and menopause stage review',
                  'Medication and supplement review',
                  'Cardiovascular and bone health discussion',
                  'Hormone therapy eligibility screening',
                  'Lifestyle and preventative health support',
                  'Lab work review when clinically appropriate',
                  'Personalized care planning',
                ].map(b => (
                  <li key={b} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-[#3D2C44]">
                    <span className="w-5 h-5 rounded-full bg-[#5B3A6B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#5B3A6B]" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#5B3A6B] text-white text-[11px] tracking-[0.25em] uppercase hover:bg-[#4A2E56] transition-all">
                Book Initial Assessment <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Follow-up */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }} className="relative p-8 sm:p-10 rounded-3xl bg-white/8 backdrop-blur-md border border-white/15">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4B4A0] mb-3">Follow-Up</div>
              <h3 className="text-[28px] leading-tight mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Follow-Up Consultation</h3>
              <div className="flex items-baseline gap-3 mt-5 mb-7">
                <span className="text-[44px] leading-none text-[#D4B4A0]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>$95</span>
                <span className="text-[12px] tracking-[0.2em] uppercase text-white/70 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 30 Minutes</span>
              </div>
              <div className="h-px bg-white/15 mb-6" />
              <ul className="space-y-3.5">
                {[
                  'Symptom reassessment',
                  'Treatment and medication follow-up',
                  'Ongoing support and education',
                  'Lifestyle optimization',
                  'Review of response to therapy',
                ].map(b => (
                  <li key={b} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-white/85">
                    <span className="w-5 h-5 rounded-full bg-[#D4B4A0]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#D4B4A0]" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#2A1F2D] text-[11px] tracking-[0.25em] uppercase hover:bg-[#FBF7F4] transition-all">
                Book Follow-Up <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          <p className="mt-10 text-center text-[12.5px] text-white/60 italic max-w-[640px] mx-auto">
            Extended follow-up appointments may be recommended depending on complexity and individualized care needs.
          </p>
        </div>
      </section>

      {/* WHY HAVEN */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center max-w-[700px] mx-auto mb-14">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">Why Haven Menopause</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Concierge-style care, built around real life.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyHaven.map((w, i) => (
              <motion.div key={w} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }} className="p-7 rounded-2xl bg-gradient-to-br from-white to-[#F4ECEF] border border-[#5B3A6B]/8 hover:shadow-[0_30px_60px_-30px_rgba(91,58,107,0.3)] transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#5B3A6B] flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                  <p className="text-[15px] leading-[1.55] text-[#2A1F2D] pt-1">{w}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PLACEHOLDER */}
      <section className="py-20 lg:py-28 bg-[#F4ECEF]">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B] mb-6">Patient Reflections</div>
            <p className="text-[clamp(1.5rem,3vw,2.5rem)] leading-[1.3] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <em className="not-italic">"I finally felt heard. Haven's pharmacist-led approach gave me clarity, a real plan, and the confidence to advocate for myself."</em>
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 text-[12px] tracking-[0.2em] uppercase text-[#5B3A6B]">
              <span className="w-10 h-px bg-[#5B3A6B]" />
              Haven Menopause Patient
              <span className="w-10 h-px bg-[#5B3A6B]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-12">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">FAQ</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-[-0.01em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Questions, answered with care.
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i
              return (
                <motion.div key={f.q} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.04 }} className={`rounded-2xl border transition-all ${open ? 'bg-white border-[#5B3A6B]/25 shadow-[0_20px_60px_-30px_rgba(91,58,107,0.3)]' : 'bg-white/60 border-[#5B3A6B]/10'}`}>
                  <button onClick={() => setOpenFaq(open ? null : i)} className="w-full px-6 sm:px-7 py-5 flex items-center justify-between gap-4 text-left">
                    <span className="text-[15px] sm:text-[16px] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{f.q}</span>
                    <span className="w-8 h-8 rounded-full bg-[#5B3A6B]/10 flex items-center justify-center flex-shrink-0">
                      {open ? <Minus className="w-4 h-4 text-[#5B3A6B]" /> : <Plus className="w-4 h-4 text-[#5B3A6B]" />}
                    </span>
                  </button>
                  <AnimatePresence>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="px-6 sm:px-7 pb-6 text-[14px] leading-[1.75] text-[#4A3D52]">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="pb-12">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="p-6 rounded-2xl bg-[#F4ECEF]/60 border border-[#5B3A6B]/10">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#5B3A6B] mt-0.5 flex-shrink-0" />
              <p className="text-[12.5px] leading-[1.7] text-[#4A3D52]">
                <span className="text-[#2A1F2D]">Disclaimer:</span> This service provides education, assessment, and pharmacist-led support. Treatment eligibility depends on health history, risk factors, current medications, and clinical appropriateness. Coverage is not guaranteed. Patients should check with their insurance provider.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-[#FBF7F4] via-[#F4ECEF] to-[#EFE6F0] relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full bg-[#D4B4A0]/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] rounded-full bg-[#8E6A9B]/20 blur-3xl" />
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5B3A6B]">Begin Today</span>
          <h2 className="mt-5 text-[clamp(2.25rem,5.5vw,4.25rem)] leading-[1.05] tracking-[-0.02em] text-[#2A1F2D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Ready for personalized<br /><em className="not-italic text-[#5B3A6B]">menopause support?</em>
          </h2>
          <p className="mt-6 text-[16px] leading-[1.7] text-[#4A3D52] max-w-[560px] mx-auto">
            Book your consultation with Haven Menopause today and take the first step toward evidence-informed, concierge-style care.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#5B3A6B] text-white text-[12px] tracking-[0.2em] uppercase hover:bg-[#4A2E56] transition-all hover:shadow-[0_15px_40px_rgba(91,58,107,0.4)]">
              Book a Menopause Consultation <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#care" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white border border-[#5B3A6B]/20 text-[#2A1F2D] text-[12px] tracking-[0.2em] uppercase hover:bg-[#F4ECEF] transition-all">
              Learn More
            </a>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2A1F2D] text-white/80">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8E6A9B] to-[#5B3A6B] flex items-center justify-center text-white text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>H</div>
              <div>
                <div className="text-[15px] tracking-[0.2em] uppercase text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Haven</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4B4A0]">Menopause</div>
              </div>
            </div>
            <p className="text-[13px] leading-[1.7] text-white/60 max-w-[320px]">
              Haven Integrative Health Consulting Inc. Evidence-informed women's health support across the Lower Mainland.
            </p>
            <div className="mt-5 flex items-center gap-2 text-[12px] text-white/50">
              <MapPin className="w-3.5 h-3.5" /> Lower Mainland, BC
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4B4A0] mb-4">Explore</div>
            <ul className="space-y-2.5 text-[13px]">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><a href="#symptoms" className="hover:text-white transition-colors">Symptoms</a></li>
              <li><a href="#care" className="hover:text-white transition-colors">Our Approach</a></li>
              <li><a href="#treatment" className="hover:text-white transition-colors">Treatment</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4B4A0] mb-4">Begin</div>
            <p className="text-[13px] leading-[1.7] text-white/60 mb-4">Concierge-style menopause care, virtual-first, with free Lower Mainland delivery.</p>
            <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#2A1F2D] text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4B4A0] transition-all">
              Book Consultation <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-6 text-[11px] tracking-[0.15em] uppercase text-white/40 flex flex-wrap items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} Haven Integrative Health Consulting Inc.</span>
            <span>Evidence-Informed · Pharmacist-Led · Lower Mainland</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
